import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Award, ArrowLeft, CheckCircle2, XCircle, Clock } from "lucide-react";
import PageShell from "@/components/brand/PageShell";
import { api } from "@/lib/api";
import LockedContent from "@/pages/LockedContent";

function formatDelta(iso) {
    if (!iso) return "";
    const target = new Date(iso).getTime();
    const now = Date.now();
    let diff = Math.max(0, target - now);
    const hrs = Math.floor(diff / 3600000);
    diff -= hrs * 3600000;
    const mins = Math.floor(diff / 60000);
    return `${hrs}h ${mins}m`;
}

export default function QuizPage() {
    const { quizId } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState({ status: "loading" });
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [tick, setTick] = useState(0);

    async function load() {
        try {
            const quiz = await api.getQuiz(quizId);
            setState({ status: "ok", quiz });
        } catch (err) {
            if (err.status === 423) {
                setState({ status: "locked", detail: err.data?.detail || err.data });
                return;
            }
            if (err.status === 402) {
                setState({ status: "inactive" });
                return;
            }
            setState({ status: "error", message: err.message });
        }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [quizId]);

    // Ticker for cooldown countdown
    useEffect(() => {
        if (state.status !== "ok" || !state.quiz.cooldown_active) return;
        const t = setInterval(() => setTick((v) => v + 1), 30000);
        return () => clearInterval(t);
    }, [state]);

    const allAnswered = useMemo(() => {
        if (state.status !== "ok") return false;
        return state.quiz.questions.every((q) => answers[q.id]);
    }, [answers, state]);

    async function handleSubmit(e) {
        e.preventDefault();
        if (!allAnswered) return;
        setSubmitting(true);
        try {
            const payload = state.quiz.questions.map((q) => ({ question_id: q.id, selected_option_id: answers[q.id] }));
            const res = await api.attemptQuiz(quizId, payload);
            setResult(res);
        } catch (err) {
            if (err.status === 423) {
                setState({ status: "locked", detail: err.data?.detail || err.data });
                return;
            }
            alert(err.message || "Unable to submit quiz.");
        } finally {
            setSubmitting(false);
        }
    }

    if (state.status === "loading") return <PageShell><div className="max-w-3xl mx-auto px-6 py-16" data-testid="quiz-loading">Loading quiz…</div></PageShell>;
    if (state.status === "locked") return <PageShell><LockedContent detail={state.detail} /></PageShell>;
    if (state.status === "inactive") {
        return <PageShell><div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="quiz-inactive">
            <h1 className="font-serif text-3xl" style={{ color: "var(--st-navy)" }}>Subscription required</h1>
            <Link to="/dashboard" className="btn-royal text-sm mt-6 inline-block">Return to dashboard</Link>
        </div></PageShell>;
    }
    if (state.status === "error") return <PageShell><div className="max-w-3xl mx-auto px-6 py-16"><p style={{ color: "#B42318" }} data-testid="quiz-error">{state.message}</p></div></PageShell>;

    const { quiz } = state;

    if (quiz.already_passed && !result) {
        return (
            <PageShell>
                <section className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="quiz-already-passed">
                    <Award size={40} strokeWidth={1.6} color="var(--st-royal)" className="mx-auto" />
                    <h1 className="font-serif text-3xl mt-4" style={{ color: "var(--st-navy)" }}>Checkpoint already passed</h1>
                    <p className="mt-3" style={{ color: "var(--st-ink-muted)" }}>The next phase is already unlocked for you.</p>
                    <Link to="/dashboard" className="btn-royal text-sm mt-6 inline-block">Back to curriculum</Link>
                </section>
            </PageShell>
        );
    }

    if (quiz.cooldown_active && !result) {
        return (
            <PageShell>
                <section className="max-w-md mx-auto px-6 py-16 text-center" data-testid="quiz-cooldown">
                    <Clock size={40} strokeWidth={1.5} color="var(--st-navy)" className="mx-auto" />
                    <h1 className="font-serif text-3xl mt-4" style={{ color: "var(--st-navy)" }}>Cooldown active</h1>
                    <p className="mt-3" style={{ color: "var(--st-ink-muted)" }}>
                        You attempted this checkpoint recently and did not meet the {Math.round(quiz.pass_threshold * 100)}% threshold.
                        You may retake it in <strong data-testid="quiz-cooldown-remaining">{formatDelta(quiz.next_attempt_allowed_at)}</strong>.
                    </p>
                    <p className="mt-2 text-xs" style={{ color: "var(--st-silver-500)" }}>
                        Use the time to review the phase materials. Retakes are unlimited after each 24-hour cooldown.
                    </p>
                    <Link to="/dashboard" className="btn-outline-navy text-sm mt-6 inline-block">Review materials</Link>
                </section>
            </PageShell>
        );
    }

    if (result) {
        const scorePct = Math.round(result.score * 100);
        return (
            <PageShell>
                <section className="max-w-3xl mx-auto px-6 py-14" data-testid="quiz-result">
                    <div className="st-surface p-8 text-center" style={{ borderColor: result.passed ? "#ABEFC6" : "#FECDCA", borderWidth: 2 }}>
                        {result.passed ? (
                            <CheckCircle2 size={48} strokeWidth={1.6} color="#067647" className="mx-auto" />
                        ) : (
                            <XCircle size={48} strokeWidth={1.6} color="#B42318" className="mx-auto" />
                        )}
                        <div className="tracking-caps text-[11px] font-semibold mt-4" style={{ color: result.passed ? "#067647" : "#B42318" }}>
                            {result.passed ? "Checkpoint passed" : "Checkpoint not passed"}
                        </div>
                        <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }} data-testid="quiz-result-score">
                            {scorePct}%
                        </h1>
                        <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                            {result.correct_count} of {result.total_count} correct · threshold {Math.round(quiz.pass_threshold * 100)}%
                        </p>
                        <p className="mt-6 text-base leading-relaxed max-w-lg mx-auto" style={{ color: "var(--st-ink)" }} data-testid="quiz-result-message">
                            {result.passed
                                ? "Checkpoint passed. The next phase is now unlocked. Continue at the pace of the curriculum, not the pace of impatience."
                                : `Score below the ${Math.round(quiz.pass_threshold * 100)}% threshold. You may retake in 24 hours. Use the time to review the phase materials.`}
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3 justify-center">
                            <Link to="/dashboard" className={result.passed ? "btn-royal text-sm" : "btn-outline-navy text-sm"} data-testid="quiz-result-back-cta">
                                Back to curriculum
                            </Link>
                        </div>
                    </div>

                    {/* Per-question review */}
                    <div className="mt-8 space-y-4" data-testid="quiz-review">
                        {result.review.map((r, i) => {
                            const q = quiz.questions.find((qq) => qq.id === r.question_id);
                            return (
                                <div key={r.question_id} className="st-surface p-5" data-testid={`quiz-review-item-${i}`}>
                                    <div className="flex items-start gap-3">
                                        {r.is_correct ? (
                                            <CheckCircle2 size={18} strokeWidth={2} color="#067647" className="mt-1 flex-shrink-0" />
                                        ) : (
                                            <XCircle size={18} strokeWidth={2} color="#B42318" className="mt-1 flex-shrink-0" />
                                        )}
                                        <div>
                                            <div className="text-sm font-semibold" style={{ color: "var(--st-navy)" }}>
                                                Question {i + 1}. {q?.question_text}
                                            </div>
                                            <div className="mt-2 text-sm" style={{ color: "var(--st-ink)" }}>
                                                <span className="font-semibold">Correct answer:</span>{" "}
                                                {q?.options.find((o) => o.id === r.correct_option_id)?.text}
                                            </div>
                                            {r.explanation && (
                                                <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                                                    {r.explanation}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </PageShell>
        );
    }

    return (
        <PageShell>
            <section className="max-w-3xl mx-auto px-6 py-14" data-testid="quiz-form-page">
                <div className="flex items-center gap-2 text-xs mb-4" style={{ color: "var(--st-silver-500)" }}>
                    <Link to="/dashboard" className="hover:underline flex items-center gap-1">
                        <ArrowLeft size={12} strokeWidth={2} /> Back
                    </Link>
                    <span className="st-divider-dot" />
                    <span className="tracking-caps font-semibold" style={{ color: "var(--st-royal)" }}>
                        Checkpoint quiz
                    </span>
                </div>

                <h1 className="font-serif text-3xl md:text-4xl" style={{ color: "var(--st-navy)" }} data-testid="quiz-title">
                    {quiz.title}
                </h1>
                <p className="mt-3 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                    Passing threshold: {Math.round(quiz.pass_threshold * 100)}% · Cooldown after a fail: {quiz.cooldown_hours} hours ·
                    Unlimited retakes after each cooldown.
                </p>

                <form onSubmit={handleSubmit} className="mt-10 space-y-6" data-testid="quiz-form">
                    {quiz.questions.map((q, i) => (
                        <div key={q.id} className="st-surface p-6" data-testid={`quiz-question-${i}`}>
                            <div className="text-sm font-semibold" style={{ color: "var(--st-navy)" }}>
                                Question {i + 1} of {quiz.questions.length}
                            </div>
                            <p className="mt-2 text-base" style={{ color: "var(--st-ink)" }}>
                                {q.question_text}
                            </p>
                            <div className="mt-4 space-y-2">
                                {q.options.map((opt) => {
                                    const checked = answers[q.id] === opt.id;
                                    return (
                                        <label
                                            key={opt.id}
                                            className={`flex items-start gap-3 p-3 rounded cursor-pointer border transition-colors`}
                                            style={{
                                                borderColor: checked ? "var(--st-royal)" : "var(--st-line)",
                                                background: checked ? "var(--royal-50, #EEF2FE)" : "var(--st-white)",
                                            }}
                                            data-testid={`quiz-option-${i}-${opt.id}`}
                                        >
                                            <input
                                                type="radio"
                                                name={q.id}
                                                value={opt.id}
                                                checked={checked}
                                                onChange={() => setAnswers((a) => ({ ...a, [q.id]: opt.id }))}
                                                className="mt-1 accent-royal"
                                            />
                                            <span className="text-sm" style={{ color: "var(--st-ink)" }}>{opt.text}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    <button
                        type="submit"
                        disabled={!allAnswered || submitting}
                        className="btn-royal w-full"
                        data-testid="quiz-submit-btn"
                    >
                        {submitting ? "Grading…" : allAnswered ? "Submit checkpoint" : "Answer every question to submit"}
                    </button>
                </form>
            </section>
        </PageShell>
    );
}
